/**
 * Audio File Size Utilities
 * Calculate estimated file sizes for different audio formats and compression levels
 */

import { 
  calculateCompressedFileSize, 
  calculateStandardWavFileSize, 
  mp3QualityToCompressionLevel,
  Mp3QualityLevel,
  getCompressionLevelDescription,
  getCompressionLevelName
} from './audioCompressionUtils';

/**
 * Calculate file size estimate for export format
 */
export const calculateExportFileSize = (
  duration: number,
  exportFormat: 'mp3' | 'wav',
  mp3Quality: Mp3QualityLevel = 128,
  wavCompression: boolean = false,
  channels: number = 2,
  sampleRate: number = 44100
): number => {
  console.log('📊 File size calculation:', { duration, exportFormat, mp3Quality, wavCompression });

  if (exportFormat === 'mp3') {
    const compressionLevel = mp3QualityToCompressionLevel(mp3Quality);
    const sizeMB = calculateCompressedFileSize(duration, compressionLevel, channels, sampleRate);
    
    console.log('📊 Compression calculation:', { 
      level: mp3Quality, 
      compressionLevel,
      totalDuration: duration.toFixed(1) + 's',
      calculatedMB: sizeMB 
    });
    
    return sizeMB;
  } else {
    return calculateStandardWavFileSize(duration, wavCompression, channels, sampleRate);
  }
};

/**
 * Get quality description for MP3-equivalent compression
 */
export const getMp3QualityDescription = (mp3Quality: Mp3QualityLevel): string => {
  const compressionLevel = mp3QualityToCompressionLevel(mp3Quality);
  return getCompressionLevelDescription(compressionLevel);
};

/**
 * Get quality name for MP3-equivalent compression
 */
export const getMp3QualityName = (mp3Quality: Mp3QualityLevel): string => {
  const compressionLevel = mp3QualityToCompressionLevel(mp3Quality);
  return getCompressionLevelName(compressionLevel);
};

/**
 * Format file size for display
 */
export const formatFileSize = (sizeInMB: number): string => {
  if (sizeInMB < 1) {
    return `${Math.round(sizeInMB * 1024)}KB`;
  } else if (sizeInMB >= 1024) {
    return `${(sizeInMB / 1024).toFixed(1)}GB`;
  } else {
    return `${sizeInMB}MB`;
  }
};

/**
 * Get compression ratios for different levels
 */
export const getCompressionRatio = (
  originalSizeMB: number,
  compressedSizeMB: number
): { ratio: number; percentage: number } => {
  const ratio = originalSizeMB / compressedSizeMB;
  const percentage = Math.round((1 - compressedSizeMB / originalSizeMB) * 100);
  return { ratio, percentage };
};
